import React, { useState } from 'react';
import { Link, useLocation as useRouterLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTrigger, DialogClose } from './ui/dialog';
import { X, ShoppingCart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import logo from '../logo_1.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useRouterLocation();
  const navigate = useNavigate();
  const { getCartItemsCount } = useCart();
  const cartItemsCount = getCartItemsCount();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="bg-white shadow-sm border-b">
      {/* Main header */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img
              src={logo}
              alt="Naga's Boutique - Elegant Sarees & Women's Clothing"
              className="h-12 sm:h-14 md:h-16 lg:h-20 xl:h-24 w-auto object-contain"
              width="250"
              height="100"
              loading="eager"
              decoding="async"
              style={{
                imageRendering: 'auto',
                maxWidth: 'clamp(120px, 25vw, 250px)',
                height: 'auto'
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-gray-700 hover:text-brand-secondary transition-colors ${isActive('/') ? 'text-brand-primary font-medium' : ''
                }`}
              style={{ '--hover-color': '#7F5F91' }}
            >
              HOME
            </Link>
            <Link
              to="/products"
              className={`text-gray-700 hover:text-brand-secondary transition-colors ${isActive('/products') ? 'text-brand-primary font-medium' : ''
                }`}
            >
              SHOP NOW
            </Link>

            <Link
              to="/offer-sales"
              className={`text-gray-700 hover:text-brand-secondary transition-colors ${isActive('/products') ? 'text-brand-primary font-medium' : ''
                }`}
            >
              OFFER SALES
            </Link>
            <Link
              to="/contact"
              className={`text-gray-700 hover:text-brand-secondary transition-colors ${isActive('/contact') ? 'text-brand-primary font-medium' : ''
                }`}
            >
              CONTACT US
            </Link>

            {/* Cart Icon */}
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 rounded-lg transition-colors hover:bg-gray-100"
              aria-label="View cart"
            >
              <ShoppingCart className="w-6 h-6" style={{ color: '#4D3B55' }} />
              {cartItemsCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: '#4D3B55' }}
                >
                  {cartItemsCount > 9 ? '9+' : cartItemsCount}
                </motion.span>
              )}
            </button>
          </nav>

          {/* Mobile Menu & Cart */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => navigate('/cart')}
              className="relative p-2 rounded-lg transition-colors"
              aria-label="View cart"
            >
              <ShoppingCart className="w-6 h-6" style={{ color: '#4D3B55' }} />
              {cartItemsCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: '#4D3B55' }}
                >
                  {cartItemsCount > 9 ? '9+' : cartItemsCount}
                </motion.span>
              )}
            </button>

            <Dialog open={isMenuOpen} onOpenChange={setIsMenuOpen}>
              <DialogTrigger asChild>
                <button
                  className="relative w-10 h-10 flex items-center justify-center rounded-lg transition-all duration-300"
                  style={{
                    backgroundColor: isMenuOpen ? '#4D3B55' : 'transparent'
                  }}
                  aria-label="Toggle menu"
                >
                  <motion.div
                    className="flex flex-col gap-1.5"
                    animate={isMenuOpen ? { rotate: 180 } : { rotate: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.span
                      className="block w-6 h-0.5 rounded-full"
                      style={{ backgroundColor: isMenuOpen ? '#FFFFFF' : '#4D3B55' }}
                      animate={isMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                    <motion.span
                      className="block w-6 h-0.5 rounded-full"
                      style={{ backgroundColor: isMenuOpen ? '#FFFFFF' : '#4D3B55' }}
                      animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
                      transition={{ duration: 0.2 }}
                    />
                    <motion.span
                      className="block w-6 h-0.5 rounded-full"
                      style={{ backgroundColor: isMenuOpen ? '#FFFFFF' : '#4D3B55' }}
                      animate={isMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.div>
                </button>
              </DialogTrigger>
              <DialogContent
                className="max-w-xs w-[85%] p-0 border-0 bg-transparent shadow-none [&>button]:hidden"
                style={{ backgroundColor: 'transparent' }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.96, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96, y: 20 }}
                  transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                  className="bg-transparent relative"
                >
                  <DialogClose asChild>
                    <button
                      className="absolute -top-12 right-0 w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 hover:scale-110 z-10"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        color: '#4D3B55'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#FFFFFF';
                        e.target.style.color = '#4D3B55';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
                        e.target.style.color = '#4D3B55';
                      }}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </DialogClose>

                  <nav className="flex flex-col px-6 py-6">
                    {[
                      { path: '/', label: 'HOME' },
                      { path: '/products', label: 'SHOP NOW' },
                      { path: '/offer-sales', label: 'OFFER SALES' },
                      { path: '/contact', label: 'CONTACT US' }
                    ].map((item, index) => (
                      <motion.div
                        key={item.path}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          delay: index * 0.08,
                          duration: 0.3,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                      >
                        <Link
                          to={item.path}
                          onClick={() => setIsMenuOpen(false)}
                          className={`group relative block py-4 px-5 rounded-xl transition-all duration-200 mb-2 ${isActive(item.path)
                              ? 'text-white'
                              : 'text-white'
                            }`}
                          style={{
                            backgroundColor: isActive(item.path) ? '#4D3B55' : 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(10px)'
                          }}
                          onMouseEnter={(e) => {
                            if (!isActive(item.path)) {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.25)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive(item.path)) {
                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                            }
                          }}
                        >
                          <span className="text-lg font-semibold tracking-wide block text-center">
                            {item.label}
                          </span>
                        </Link>
                      </motion.div>
                    ))}
                  </nav>
                </motion.div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
